import { Button, Heading, Spinner, Text } from "@radix-ui/themes";
import { ReviewsCarousel } from "@views/login/components/login-testimonials";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FallingPictures } from "./falling-pictures";
import { FEATURE_OPTIONS, SOURCE_OPTIONS } from "./onboarding-constants";
import { TagsCloud } from "./tags-cloud";

// Greeting step with bottom to top animation and auto-transition
export const GreetingStep = ({
  onContinue,
  userName,
}: {
  onContinue: (features: string[], sources: string[]) => void;
  userName?: string;
}) => {
  const { t } = useTranslation();
  const [showLoading, setShowLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [features, setFeatures] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);

  useEffect(() => {
    setTimeout(() => {
      setShowLoading(true);
    }, 100);
    setTimeout(() => {
      setShowLoading(false);
    }, 4500);
    setTimeout(() => {
      setShowLoading(false);
      setShowWelcome(true);
    }, 5000);
    setTimeout(() => {
      setShowQuestions(true);
    }, 6000);
  }, []);

  return (
    <div className="flex w-full md:min-h-[80vh] items-center justify-center max-w-6xl mx-auto">
      <div className="grow p-4 max-w-2xl">
        <AnimatePresence>
          {showLoading && (
            <motion.div
              key="step-0"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.5 }}
              exit={{ opacity: 0, height: 0 }}
            >
              <FallingPictures pictures={[]} />
              <Spinner className="m-auto z-0" />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showWelcome && (
            <motion.div
              key="step-0"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.5 }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="md:h-0 h-[20vh]" />
              <Heading size="8" className="mb-4">
                {userName
                  ? t("welcome.greetings.welcome_message", { userName })
                  : t("welcome.greetings.welcome_message_fallback")}
              </Heading>

              <Text className="block">
                {t("welcome.greetings.lets_get_started")}
              </Text>
              <div className="h-12" />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showQuestions && (
            <motion.div
              key="step-1"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.5 }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Heading size="6" className="mb-6">
                {t("welcome.greetings.what_looking_for")}
              </Heading>

              <TagsCloud
                rawTagOptions={FEATURE_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: "label" in opt ? opt.label : t(opt.key),
                }))}
                onChange={(val) => {
                  if (features?.length === 0) {
                    setTimeout(() => {
                      window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: "smooth",
                      });
                    }, 200);
                  }
                  setFeatures(val);
                }}
              />
              <div className="h-12" />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {features?.length > 0 && (
            <motion.div
              key="step-2"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.5 }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Heading size="6" className="mb-6">
                {t("welcome.greetings.how_did_you_hear")}
              </Heading>

              <TagsCloud
                rawTagOptions={SOURCE_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: t(opt.key),
                }))}
                onChange={(val) => {
                  if (sources?.length === 0) {
                    setTimeout(() => {
                      window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: "smooth",
                      });
                    }, 200);
                  }
                  setSources(val);
                }}
              />
              <div className="h-12" />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {sources?.length > 0 && (
            <motion.div
              key="step-3"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.5 }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Text className="mb-6 block">
                {t("welcome.greetings.thanks_for_sharing")}
              </Text>

              <Button
                size="3"
                onClick={() => onContinue(features, sources)}
                className="hue-animation"
              >
                {t("welcome.greetings.continue_button")}
              </Button>
              <div className="h-12" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {false && (
        <div className="w-1/2 pr-4 shrink-0">
          <div className="rounded-lg bg-slate-800 h-available-screen-height">
            <ReviewsCarousel />
          </div>
        </div>
      )}
    </div>
  );
};
